// ============= Imports =============
import { SignalRContextProps } from '@/contexts/SignalRContext';
import {
  toastHistoryStore,
  getStackTrace,
} from '@/components/common/Toast/ToastHistory';

// ============= Constants =============
// Hub name must match the hub name configured in environment variables
// e.g., VITE_SIGNALR_HUB_PATH_WORKSPACE_RELAY_HUB=/hubs/WorkspaceRelayHub
const HUB_NAME = 'workspaceRelayHub';

// ============= Types =============
export interface AddClientToGroupWorkspaceSessionProps {
  invokeHub: SignalRContextProps['invokeHub'];
  workspaceId: string;
  userId: string;
}

// ============= Main Function =============
/**
 * Adds the client to a workspace group in WorkspaceRelayHub
 * @param invokeHub Function to invoke SignalR hub methods
 * @param workspaceId The workspace ID to join
 * @param userId The user ID to join
 * @returns Promise with API response or error
 */
export const addClientToGroupWorkspaceSession = async ({
  invokeHub,
  workspaceId,
  userId,
}: AddClientToGroupWorkspaceSessionProps): Promise<any> => {
  try {
    console.log(
      `🔄 Adding client to group [${workspaceId}] in WorkspaceRelayHub...`
    );

    if (!workspaceId || !userId) {
      throw new Error('workspaceId and userId are required');
    }

    // Log outbound invocation (log only; not a user-facing toast)
    try {
      toastHistoryStore.addLogEntry({
        type: 'info',
        message: `[SignalR Invoke] ${HUB_NAME}.AddClientToGroupAsync`,
        direction: 'outgoing',
        source: JSON.stringify(
          {
            hub: HUB_NAME,
            target: 'AddClientToGroupAsync',
            arguments: [workspaceId, userId],
            stack: getStackTrace(),
          },
          null,
          2
        ),
      });
    } catch {
      // Never let logging break the app
    }

    const result = await invokeHub<any>(
      HUB_NAME,
      'AddClientToGroupAsync',
      workspaceId,
      userId
    );

    console.log(`✅ Client added to group [${workspaceId}] successfully`);
    return result;
  } catch (error) {
    console.error(
      `🔴 Error adding client to group [${workspaceId}] in WorkspaceRelayHub:`,
      error
    );

    // Log error (log only; not a user-facing toast)
    try {
      toastHistoryStore.addLogEntry({
        type: 'error',
        message: `[SignalR Invoke Error] ${HUB_NAME}.AddClientToGroupAsync for workspace ${workspaceId}`,
        direction: 'outgoing',
        source:
          (error instanceof Error && error.stack) ||
          JSON.stringify(
            {
              hub: HUB_NAME,
              target: 'AddClientToGroupAsync',
              arguments: [workspaceId, userId],
              error: String(error),
            },
            null,
            2
          ),
      });
    } catch {
      // Swallow logging errors
    }
    throw error;
  }
};
